import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  listMcqQuestions, createMcqQuestion, updateMcqQuestion, deleteMcqQuestion,
  listCodingQuestions, createCodingQuestion, updateCodingQuestion, deleteCodingQuestion,
  confirmQuestionsUpload,
  getRecruiterJob, updateRecruiterJobQuestions
} from "../services/jobService";
import { QuestionSourceManager } from "./QuestionGenControls";
import { InterviewQuestionSourceManager } from "./InterviewQuestionSourceManager";
import "./QuestionBankManager.css";

/* ── AI Interview question manager ─────────────────────────────────────────
   Interview questions live as a plain uploadedQuestions array on the job
   doc (no Firestore subcollection like MCQ/Coding), so this reads/writes
   via GET/PATCH /recruiter/jobs/{jobId} instead of the question-bank CRUD
   endpoints above. Same tab bar + always-editable list as MCQ/Coding, for a
   consistent experience across all three job types. ── */

function InterviewQuestionManager({ jobId, companyName, onClose, initialRole = "", initialJdText = "" }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState("");
  const [jdText, setJdText] = useState("");
  const [difficulty, setDifficulty] = useState("moderate");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const job = await getRecruiterJob(jobId);
      setQuestions(job?.uploadedQuestions || []);
      // Falls back to the pipeline job's already-entered role/JD
      // (AssessmentManagerModal.jsx's inheritance, mirrored here) only when
      // this job's own config has never been set — never overrides a value
      // the recruiter already saved for this specific job.
      setRole(job?.config?.role || initialRole);
      setJdText(job?.config?.jd_text || initialJdText);
      setDifficulty(job?.config?.difficulty || "moderate");
    } catch (err) {
      toast.error(err.message || "Unable to load questions");
    } finally {
      setLoading(false);
    }
  }, [jobId, initialRole, initialJdText]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (questions.length === 0) {
      toast.error("Add at least one question.");
      return;
    }
    setSaving(true);
    try {
      await updateRecruiterJobQuestions(jobId, questions, { role, jdText, difficulty });
      toast.success("Questions saved");
    } catch (err) {
      toast.error(err.message || "Unable to save questions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="qbm">
      {loading ? (
        <p className="qbm-empty">Loading questions…</p>
      ) : (
        <InterviewQuestionSourceManager
          companyName={companyName}
          role={role} onRoleChange={setRole}
          jdText={jdText} onJdTextChange={setJdText}
          difficulty={difficulty} onDifficultyChange={setDifficulty}
          questions={questions} onQuestionsChange={setQuestions}
        />
      )}

      <div className="recruiter-modal-actions">
        {onClose && <button type="button" className="secondary-button" onClick={onClose}>Close</button>}
        <button type="button" className="primary-button" onClick={handleSave} disabled={saving || loading}>
          {saving ? "Saving…" : "Save questions"}
        </button>
      </div>
    </div>
  );
}

/* ── Main manager — dispatches by event type, calls no hooks itself ───────── */

export default function QuestionBankManager({
  jobId, eventType, includeCoding = false, companyName = "", onClose,
  initialRole = "", initialExperience = "", initialJdText = ""
}) {
  if (!eventType || eventType === "INTERVIEW") {
    return (
      <InterviewQuestionManager
        jobId={jobId} companyName={companyName} onClose={onClose}
        initialRole={initialRole} initialJdText={initialJdText}
      />
    );
  }
  return (
    <McqCodingQuestionManager
      jobId={jobId} eventType={eventType} includeCoding={includeCoding}
      companyName={companyName} onClose={onClose}
      initialRole={initialRole} initialExperience={initialExperience} initialJdText={initialJdText}
    />
  );
}

function McqCodingQuestionManager({
  jobId, eventType, includeCoding = false, companyName = "", onClose,
  initialRole = "", initialExperience = "", initialJdText = ""
}) {
  const combined = eventType === "MCQ" && includeCoding;
  const [activeBank, setActiveBank] = useState(eventType === "CODING" ? "coding" : "mcq");
  const isMcq = activeBank === "mcq";
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = isMcq ? await listMcqQuestions(jobId) : await listCodingQuestions(jobId);
      setQuestions(qs);
    } catch (err) {
      toast.error(err.message || "Unable to load questions");
    } finally {
      setLoading(false);
    }
  }, [jobId, isMcq]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (q) => {
    await (isMcq ? createMcqQuestion(jobId, q) : createCodingQuestion(jobId, q));
    load();
  };

  const handleUpdate = async (index, existing, updated) => {
    await (isMcq
      ? updateMcqQuestion(jobId, existing.question_id, updated)
      : updateCodingQuestion(jobId, existing.question_id, updated));
    load();
  };

  const handleRemove = async (index, existing) => {
    // Not awaited by the caller (QuestionRow's onClick fires it directly),
    // so this must handle its own errors rather than relying on an upstream
    // try/catch like handleAdd/handleUpdate do via their form's onSave.
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    try {
      await (isMcq ? deleteMcqQuestion(jobId, existing.question_id) : deleteCodingQuestion(jobId, existing.question_id));
      toast.success("Question deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Unable to delete question");
    }
  };

  const handleBulkAdd = async (items) => {
    await confirmQuestionsUpload(jobId, isMcq ? "mcq" : "coding", items);
    load();
  };

  return (
    <div className="qbm">
      {combined && (
        <div className="qbm-tabs">
          <button
            type="button"
            className={`qbm-tab${activeBank === "mcq" ? " active" : ""}`}
            onClick={() => setActiveBank("mcq")}
          >
            MCQ Questions
          </button>
          <button
            type="button"
            className={`qbm-tab${activeBank === "coding" ? " active" : ""}`}
            onClick={() => setActiveBank("coding")}
          >
            Coding Questions
          </button>
        </div>
      )}

      {loading ? (
        <p className="qbm-empty">Loading questions…</p>
      ) : (
        <QuestionSourceManager
          eventType={isMcq ? "MCQ" : "CODING"}
          companyName={companyName}
          questions={questions}
          onAdd={handleAdd}
          onUpdate={handleUpdate}
          onRemove={handleRemove}
          onBulkAdd={handleBulkAdd}
          initialRole={initialRole}
          initialExperience={initialExperience}
          initialJdText={initialJdText}
          jobId={jobId}
        />
      )}

      {onClose && (
        <div className="recruiter-modal-actions">
          <button type="button" className="primary-button" onClick={onClose}>Done</button>
        </div>
      )}
    </div>
  );
}
